/**
 * Submission Triage — "Save View" modal (design OR-1507).
 *
 * Opened from "+ Save current view" in the Saved Views menu. One required
 * field (the title) capped at 40 characters, with the design's live
 * "N characters left" counter that turns into the error treatment at
 * zero. Every string here is the design's, verbatim.
 */
import { useEffect, useState } from "react";
import {
    Dialog as AriaDialog,
    Modal as AriaModal,
    ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { XClose } from "@untitledui/icons";
import { Button, Input, cx } from "@tricura/atlas-ui";
import { MAX_VIEW_NAME_LENGTH } from "./view-preference-config";

interface SaveViewModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** Resolves once the view is stored. */
    onSave: (name: string) => void;
}

export function SaveViewModal({ isOpen, onOpenChange, onSave }: SaveViewModalProps) {
    const [name, setName] = useState("");
    /** The design's error frame shows "This is a required field" while
     *  "Save view" is still disabled, so the message can't be coming from
     *  a submit — it fires when the user leaves the field empty. Never on
     *  an untouched form. */
    const [showError, setShowError] = useState(false);

    // Every open starts from a blank form.
    useEffect(() => {
        if (isOpen) {
            setName("");
            setShowError(false);
        }
    }, [isOpen]);

    const trimmed = name.trim();
    const remaining = MAX_VIEW_NAME_LENGTH - name.length;
    const isEmpty = trimmed.length === 0;
    const invalid = showError && isEmpty;
    /** The design puts the field itself into the error treatment once the
     *  title hits the cap — red border and alert glyph — even though
     *  "Save view" stays enabled. It reads as "you're at the limit", not
     *  as a blocker. */
    const atLimit = remaining === 0;

    const submit = () => {
        if (isEmpty) {
            setShowError(true);
            return;
        }
        onSave(trimmed);
        onOpenChange(false);
    };

    return (
        <AriaModalOverlay
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            isDismissable
            // `bg-overlay/70` is the design's token for this scrim
            // (#0A0A0A at 70%) and what every other modal in the app
            // already uses. No backdrop blur: the design keeps the page
            // behind it sharp.
            //
            // Sits above the Saved Views popover (z-50) that opened it,
            // so the scrim covers it regardless of portal order.
            className="bg-overlay/70 fixed inset-0 z-[1000] flex items-center justify-center p-4"
        >
            <AriaModal className="w-full max-w-[400px]">
                <AriaDialog className="bg-primary relative rounded-xl p-6 shadow-xl outline-none">
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={() => onOpenChange(false)}
                        className="text-fg-quaternary hover:text-fg-secondary absolute right-4 top-4 cursor-pointer rounded-md p-1 transition duration-100 ease-linear"
                    >
                        <XClose aria-hidden="true" className="size-5" />
                    </button>

                    <h2 className="text-primary text-md font-semibold">Save View</h2>
                    <p className="text-tertiary mt-1 text-sm">
                        Save your current filters and/or columns as a view you can reuse anytime.
                    </p>

                    <div className="mt-5">
                        <Input
                            size="sm"
                            isRequired
                            label="Title"
                            placeholder="e.g. High Priority"
                            value={name}
                            onChange={(next) => {
                                setName(next);
                                // Typing clears the message immediately;
                                // it comes back only on another empty blur.
                                if (next.trim()) setShowError(false);
                            }}
                            onBlur={() => setShowError(name.trim().length === 0)}
                            maxLength={MAX_VIEW_NAME_LENGTH}
                            isInvalid={invalid || atLimit}
                            aria-label="Title"
                            data-autotest="triage-save-view-input-title"
                            onKeyDown={(event) => {
                                if (event.key === "Enter") submit();
                            }}
                        />
                        <p
                            className={cx(
                                "mt-1.5 text-xs",
                                invalid || atLimit ? "text-error-primary" : "text-tertiary",
                            )}
                        >
                            {invalid ? "This is a required field" : `${remaining} characters left`}
                        </p>
                    </div>

                    <div className="mt-6 flex items-center gap-3">
                        <Button
                            color="secondary"
                            size="md"
                            className="flex-1"
                            onClick={() => onOpenChange(false)}
                            data-autotest="triage-save-view-button-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            size="md"
                            className="flex-1"
                            isDisabled={isEmpty}
                            onClick={submit}
                            data-autotest="triage-save-view-button-save"
                        >
                            Save view
                        </Button>
                    </div>
                </AriaDialog>
            </AriaModal>
        </AriaModalOverlay>
    );
}
